import {
  queryOptions,
  useQuery,
  type UseQueryResult,
} from "@tanstack/react-query";

import type { ApiClient } from "@/api/client";
import { useApiClient } from "@/api/client-context";
import { qk } from "@/api/query-keys";
import { CASE_CONFIG } from "@/domain/case/case-config";
import { epochToDate } from "@/domain/case/map-process-instance";
import { formatCaseNumber } from "@/lib/format/case-number";

/** 「今、自分（または所属グループ）の番」の承認待ち 1 件。 */
export type PendingApproval = {
  readonly caseId: number;
  readonly caseNumber: string;
  readonly taskGuid: string;
  readonly processModelId: string;
  readonly processModelDisplayName: string;
  /** 現在ステップの表示名（例: 部長確認）。 */
  readonly stepLabel: string | null;
  readonly applicant: string | null;
  readonly requestedAt: Date | null;
};

type SpiffTaskRow = {
  readonly id: number;
  readonly process_instance_id: number;
  readonly task_guid?: string | null;
  readonly task_title?: string | null;
  readonly task_name?: string | null;
  readonly process_model_identifier: string;
  readonly process_model_display_name: string;
  readonly process_initiator_username?: string | null;
  readonly created_at_in_seconds?: number | null;
};

type SpiffTaskListResponse = {
  readonly results?: readonly SpiffTaskRow[];
};

/**
 * 承認待ち一覧。自分宛（/tasks）と所属グループ宛（/tasks/for-my-groups）を統合する。
 *
 * **起案タスクは除く**。backend は「未完了かつ自分が担当」でしか絞らないので
 * （`tasks_controller.task_list_my_tasks`）、自分の下書き・差し戻しの再入力まで
 * ここに載ってくる。判断すべき他人の申請だけを残す。
 * 除く基準は `CASE_CONFIG.draftTaskNames`（`Activity_Draft`）。要対応・差し戻し判定と
 * 同じ規約を使い、基準を二重に持たない。規約から外れたモデルの起票者向けタスクは
 * ここに残るが、マイ案件の要対応にも出るので見落としにはならない。
 *
 * 起票者かどうかでは絞らない。SoD はモデルの `lane_owners` で担保する設計で、
 * レーンを持たないモデル（デモ 10 フロー）まで一覧から消すと確認できなくなる。
 *
 * グループ宛は権限次第で 403 になり得るので、失敗しても自分宛だけで表示する
 * （承認できる案件が 1 件も出ないより、見える分を出す方が実害が小さい）。
 */
export function pendingApprovalsQueryOptions(client: ApiClient) {
  const query = { per_page: 100, page: 1 };

  const fetchApprovals = async (): Promise<readonly PendingApproval[]> => {
    const [mine, groups] = await Promise.all([
      client.get<SpiffTaskListResponse>("/tasks", { query }),
      client
        .get<SpiffTaskListResponse>("/tasks/for-my-groups", { query })
        .catch(() => ({ results: [] }) as SpiffTaskListResponse),
    ]);

    const byGuid = new Map<string, PendingApproval>();
    for (const row of [...(mine.results ?? []), ...(groups.results ?? [])]) {
      if (isDraftTask(row)) continue;

      const taskGuid = row.task_guid ?? String(row.id);
      if (byGuid.has(taskGuid)) continue;

      byGuid.set(taskGuid, {
        caseId: row.process_instance_id,
        caseNumber: formatCaseNumber(row.process_instance_id),
        taskGuid,
        processModelId: row.process_model_identifier,
        processModelDisplayName: row.process_model_display_name,
        stepLabel: row.task_title ?? row.task_name ?? null,
        applicant: row.process_initiator_username ?? null,
        requestedAt: epochToDate(row.created_at_in_seconds),
      });
    }

    // 依頼が古い順。待たせている案件を先に出す。
    return [...byGuid.values()].sort(
      (a, b) =>
        (a.requestedAt?.getTime() ?? 0) - (b.requestedAt?.getTime() ?? 0),
    );
  };

  return queryOptions({
    queryKey: qk.tasks.forMe({ scope: "pending-approvals" }),
    queryFn: fetchApprovals,
    staleTime: 30_000,
  });
}

export function usePendingApprovals(): UseQueryResult<
  readonly PendingApproval[]
> {
  return useQuery(pendingApprovalsQueryOptions(useApiClient()));
}

/** 起案タスクか（BPMN の id で判定する。表示名はモデルごとに違う）。 */
function isDraftTask(row: SpiffTaskRow): boolean {
  const name = row.task_name;
  if (name === null || name === undefined) return false;

  return CASE_CONFIG.draftTaskNames.includes(name);
}
